import PptxGenJS from 'pptxgenjs';

// ── Brand colours ──────────────────────────────────────────────────────────
const COLORS = {
  Objectives:  '0D4F5C',
  Explore:     '2AABB8',
  Tafakkur:    '2AABB8',
  Compare:     '1A7A8A',
  Tadabbur:    '1A7A8A',
  Question:    '3A6B8A',
  "Iʿtibār":  '3A6B8A',
  Connect:     'C9A84C',
  Tadhakkur:   'C9A84C',
  Appreciate:  '0D4F5C',
  "Khilāfah":  '0D4F5C',
  Activities:  '1A7A8A',
};

// ── Subtitle per contentKey ────────────────────────────────────────────────
const SUBTITLES = {
  learningObjectives:               'Learning Objectives',
  content:                          'Content',
  explanation:                      'Explanation',
  observations:                     'Observations',
  fascinatingFacts:                 'Fascinating Facts',
  analogy:                          'Analogy',
  comparison:                       'Comparison',
  negation_of_chance:               'Negation of Chance',
  negation_of_material_causes:      'Negation of Material Causes',
  negation_of_nature:               "Negation of 'Nature'",
  negation_of_autonomous_processes: 'Negation of Autonomous Processes',
  conclusion:                       'Conclusion',
  historicalLesson:                 'Historical Lesson',
  moralLesson:                      'Moral Lesson',
  interconnectedness:               'Interconnectedness',
  completeDependency:               'Complete Dependency',
  interdependency:                  'Interdependency',
  allahNames:                       "Allah's Names",
  whatIfs:                          'What Ifs',
  zikrFikrShukr:                    'Zikr · Fikr · Shukr',
  characterLessons:                 'Character Lessons',
  connectWithQuran:                 'Connect With Quran',
  connectWithHadith:                'Connect With Hadith',
  dua:                              'Dua',
  reflectionQuestions:              'Reflection Questions',
  designWisdom:                     'Design Wisdom',
  continuousCreation:               'Continuous Creation',
  revelationAnchor:                 'Revelation Anchor',
  muslimScholar:                    'Muslim Scholar Spotlight',
  creationVsDesign:                 'Creation vs Human Design',
  stewardshipActions:               'Stewardship Actions',
  main:                             'Activities',
};

// LAYOUT_WIDE = 13.33 × 7.5 inches
const W = 13.33;
const H = 7.5;
const PAD = 0.45;
const TITLE_H = 0.85;
const BODY_Y = PAD + TITLE_H + 0.15;
const BODY_H = H - BODY_Y - PAD;
const BODY_W = W - PAD * 2;

// ── Fetch logo as base64 at runtime ───────────────────────────────────────
async function fetchLogoBase64() {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/amfi-logo.png`);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result); // "data:image/png;base64,..."
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
}

// ── Parse **bold** markdown into pptxgenjs rich-text array ────────────────
function parseMarkdown(text = '') {
  if (!text) return [{ text: '', options: {} }];
  const parts = [];
  const regex = /\*\*(.+?)\*\*/g;
  let last = 0, match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push({ text: text.slice(last, match.index), options: {} });
    parts.push({ text: match[1], options: { bold: true } });
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push({ text: text.slice(last), options: {} });
  return parts.length ? parts : [{ text, options: {} }];
}

// Convert any value to a flat string (used for non-body-text contexts)
function toLines(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join('\n');
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${capitalize(k)}:\n${toLines(v)}`)
      .join('\n\n');
  }
  return String(value);
}

const capitalize = (s = '') =>
  s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ── Layout helpers ─────────────────────────────────────────────────────────
function addTitleBar(slide, pptx, dimension, subtitle, logoData) {
  const color = COLORS[dimension] || '0D4F5C';
  slide.addShape(pptx.ShapeType.rect, {
    x: PAD, y: PAD * 0.5, w: W - PAD * 2, h: TITLE_H,
    fill: { color },
    line: { color: 'FFFFFF' },
  });
  // Leave room for logo on the right
  slide.addText(`${dimension}  —  ${subtitle}`, {
    x: PAD + 0.15, y: PAD * 0.5, w: W - PAD * 2 - 1.2, h: TITLE_H,
    fontSize: 26, bold: true, color: 'FFFFFF',
    align: 'left', valign: 'middle',
  });
  if (logoData) {
    slide.addImage({
      data: logoData,
      x: W - PAD - 0.95, y: PAD * 0.5 + 0.05,
      w: 0.75, h: 0.75,
    });
  }
  slide.background = { fill: 'FFFFFF' };
}

// Add body text that properly renders **bold** markdown
function addBody(slide, rawText, opts = {}) {
  const richText = [];
  const lines = (rawText || '').split('\n');
  lines.forEach((line, li) => {
    const parsed = parseMarkdown(line);
    richText.push(...parsed);
    if (li < lines.length - 1) richText.push({ text: '\n', options: {} });
  });
  slide.addText(richText, {
    x: PAD, y: BODY_Y, w: BODY_W, h: BODY_H,
    fontSize: 17, color: '1A1A1A',
    align: 'left', valign: 'top',
    wrap: true, autoFit: true,
    ...opts,
  });
}

// ── Per-slide rendering ────────────────────────────────────────────────────
function renderSlide(pptx, slideData, logoData) {
  const { dimension, contentKey } = slideData;
  const subtitle = SUBTITLES[contentKey] || capitalize(contentKey);

  // ── Activities ──────────────────────────────────────────────────────────
  if (dimension === 'Activities') {
    (slideData.activities || []).forEach((act) => {
      const s = pptx.addSlide();
      addTitleBar(s, pptx, 'Activities', act.title || 'Activity', logoData);
      const lines = [];
      if (act.dimension)  lines.push(`Stage: ${act.dimension}`, '');
      if (act.objective)  lines.push('Objective:', act.objective, '');
      if (act.materials?.length)    lines.push('Materials:', ...act.materials.map(m => `• ${m}`), '');
      if (act.instructions?.length) lines.push('Instructions:', ...act.instructions.map((ins, i) => `${i + 1}. ${ins}`), '');
      addBody(s, lines.join('\n'));
    });
    return;
  }

  const s = pptx.addSlide();
  addTitleBar(s, pptx, dimension, subtitle, logoData);

  // ── allahNames table ─────────────────────────────────────────────────────
  if (contentKey === 'allahNames') {
    const names = slideData.allahNames || {};
    const en = names.namesInEnglish || [];
    const ar = names.namesInArabic  || [];
    const wt = names.whatItTells    || [];
    const hdrColor = COLORS[dimension] || '0D4F5C';
    const rows = [
      [
        { text: 'Name (English)', options: { bold: true, color: 'FFFFFF', fill: { color: hdrColor } } },
        { text: 'Name (Arabic)',  options: { bold: true, color: 'FFFFFF', fill: { color: hdrColor } } },
        { text: 'What it reveals about Allah', options: { bold: true, color: 'FFFFFF', fill: { color: hdrColor } } },
      ],
      ...en.map((name, i) => [name || '', ar[i] || '', wt[i] || '']),
    ];
    s.addTable(rows, {
      x: PAD, y: BODY_Y, w: BODY_W,
      fontSize: 14, color: '222222', align: 'left',
      border: { type: 'solid', color: 'DDDDDD', pt: 0.5 },
      autoPage: true,
    });
    return;
  }

  // ── zikrFikrShukr table ──────────────────────────────────────────────────
  if (contentKey === 'zikrFikrShukr') {
    const zfs = slideData.zikrFikrShukr || {};
    const zikr  = zfs.zikr  || [];
    const fikr  = zfs.fikr  || [];
    const shukr = zfs.shukr || [];
    const maxLen = Math.max(zikr.length, fikr.length, shukr.length);
    const hdrColor = COLORS[dimension] || '0D4F5C';
    const rows = [
      [
        { text: 'Zikr (Remembrance)', options: { bold: true, color: 'FFFFFF', fill: { color: hdrColor } } },
        { text: 'Fikr (Reflection)',   options: { bold: true, color: 'FFFFFF', fill: { color: hdrColor } } },
        { text: 'Shukr (Gratitude)',   options: { bold: true, color: 'FFFFFF', fill: { color: hdrColor } } },
      ],
      ...Array.from({ length: maxLen }, (_, i) => [zikr[i] || '', fikr[i] || '', shukr[i] || '']),
    ];
    s.addTable(rows, {
      x: PAD, y: BODY_Y, w: BODY_W,
      fontSize: 14, color: '222222', align: 'left',
      border: { type: 'solid', color: 'DDDDDD', pt: 0.5 },
      autoPage: true,
    });
    return;
  }

  // ── connectWithQuran ─────────────────────────────────────────────────────
  if (contentKey === 'connectWithQuran') {
    const text = (slideData.connectWithQuran || []).map(v =>
      [`${v.verse || ''}`, v.reference ? `(${v.reference})` : '', `Translation: ${v.translation || ''}`, v.explanation || ''].filter(Boolean).join('\n')
    ).join('\n\n─────────────────────────\n\n');
    addBody(s, text || 'No Qur\'anic connection provided.');
    return;
  }

  // ── connectWithHadith ────────────────────────────────────────────────────
  if (contentKey === 'connectWithHadith') {
    const text = (slideData.connectWithHadith || []).map(h =>
      [`"${h.hadith || ''}"`, h.explanation || ''].filter(Boolean).join('\n')
    ).join('\n\n─────────────────────────\n\n');
    addBody(s, text || 'No hadith connection provided.');
    return;
  }

  // ── revelationAnchor ─────────────────────────────────────────────────────
  if (contentKey === 'revelationAnchor') {
    const ra = slideData.revelationAnchor || {};
    const text = [
      ra.verse       ? `Arabic: ${ra.verse}` : '',
      ra.reference   ? `Reference: ${ra.reference}` : '',
      ra.translation ? `Translation: ${ra.translation}` : '',
      ra.reflection  ? `Reflection: ${ra.reflection}` : '',
    ].filter(Boolean).join('\n\n');
    addBody(s, text);
    return;
  }

  // ── muslimScholar ────────────────────────────────────────────────────────
  if (contentKey === 'muslimScholar') {
    const ms = slideData.muslimScholar || {};
    const text = [
      ms.name         ? `Scholar: ${ms.name}` : '',
      ms.era          ? `Era: ${ms.era}` : '',
      ms.field        ? `Field: ${ms.field}` : '',
      ms.contribution ? `Contribution:\n${ms.contribution}` : '',
      ms.connection   ? `Connection to Topic:\n${ms.connection}` : '',
    ].filter(Boolean).join('\n\n');
    addBody(s, text);
    return;
  }

  // ── creationVsDesign ─────────────────────────────────────────────────────
  if (contentKey === 'creationVsDesign') {
    const cvd = slideData.creationVsDesign || {};
    const text = [
      cvd.humanDesign  ? `Human Design:\n${toLines(cvd.humanDesign)}` : '',
      cvd.divineDesign ? `Divine Creation:\n${toLines(cvd.divineDesign)}` : '',
      cvd.gap          ? `The Gap:\n${toLines(cvd.gap)}` : '',
    ].filter(Boolean).join('\n\n─────────────────────────\n\n');
    addBody(s, text || toLines(cvd));
    return;
  }

  // ── characterLessons ─────────────────────────────────────────────────────
  if (contentKey === 'characterLessons') {
    const cl = slideData.characterLessons;
    let text = '';
    if (typeof cl === 'object' && !Array.isArray(cl)) {
      text = Object.entries(cl).map(([k, v]) => `${capitalize(k)}:\n${toLines(v)}`).join('\n\n');
    } else {
      text = toLines(cl);
    }
    addBody(s, text);
    return;
  }

  // ── stewardshipActions ───────────────────────────────────────────────────
  if (contentKey === 'stewardshipActions') {
    addBody(s, (slideData.stewardshipActions || []).map((a, i) => `${i + 1}. ${toLines(a)}`).join('\n\n'));
    return;
  }

  // ── Objectives ───────────────────────────────────────────────────────────
  if (contentKey === 'learningObjectives') {
    const lo = slideData.learningObjectives || {};
    const lines = Object.entries(lo).flatMap(([cat, items]) => [
      `${capitalize(cat)}:`,
      ...(Array.isArray(items) ? items : [items]).map(item => `  • ${item}`),
      '',
    ]);
    addBody(s, lines.join('\n'));
    return;
  }

  // ── questions array ──────────────────────────────────────────────────────
  if (slideData.questions && Array.isArray(slideData.questions)) {
    addBody(s, slideData.questions.map((q, i) => `${i + 1}. ${q}`).join('\n\n'));
    return;
  }

  // ── fascinatingFacts ─────────────────────────────────────────────────────
  if (contentKey === 'fascinatingFacts') {
    const facts = Array.isArray(slideData.fascinatingFacts)
      ? slideData.fascinatingFacts
      : [slideData.fascinatingFacts].filter(Boolean);
    addBody(s, facts.map((f, i) => `${i + 1}. ${f}`).join('\n\n'));
    return;
  }

  // ── reflectionQuestions ──────────────────────────────────────────────────
  if (contentKey === 'reflectionQuestions') {
    const rq = Array.isArray(slideData.reflectionQuestions)
      ? slideData.reflectionQuestions
      : [slideData.reflectionQuestions].filter(Boolean);
    addBody(s, rq.map((q, i) => `${i + 1}. ${q}`).join('\n\n'));
    return;
  }

  // ── Generic fallback ─────────────────────────────────────────────────────
  const SKIP = new Set(['id', 'dimension', 'contentkey', 'subtopicindex']);
  const parts = [];
  for (const [k, v] of Object.entries(slideData)) {
    if (SKIP.has(k.toLowerCase()) || v == null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    parts.push(toLines(v));
  }
  addBody(s, parts.join('\n\n') || '(No content)');
}

// ── Main export ────────────────────────────────────────────────────────────
export const exportAmfiAsPptx = async (slides, topic, framework) => {
  if (!slides?.length) { alert('No lesson to export yet.'); return; }

  const logoData = await fetchLogoBase64();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE'; // 13.33 × 7.5 in

  // Cover slide
  const cover = pptx.addSlide();
  cover.background = { fill: '0D4F5C' };
  if (logoData) {
    cover.addImage({ data: logoData, x: 5.67, y: 0.6, w: 2, h: 2 });
  }
  cover.addText('AMFI Lesson Plan', {
    x: 0.5, y: 2.9, w: W - 1, h: 1,
    fontSize: 40, bold: true, color: 'FFFFFF', align: 'center',
  });
  cover.addText(topic || 'Untitled Lesson', {
    x: 0.5, y: 4.0, w: W - 1, h: 0.9,
    fontSize: 28, color: 'E8C97A', align: 'center',
  });
  cover.addText(
    framework === 'pure'
      ? 'Pure AMFI — Qur\'anic Learning Chain'
      : 'AMFI × 5DT — Integrated Framework',
    { x: 0.5, y: 5.0, w: W - 1, h: 0.6, fontSize: 18, color: 'AADDDD', align: 'center' }
  );

  slides.forEach(slide => renderSlide(pptx, slide, logoData));

  const filename = `AMFI_${(topic || 'Lesson').replace(/\s+/g, '_')}_${framework === 'pure' ? 'PureAMFI' : 'AMFI5DT'}.pptx`;
  pptx.writeFile({ fileName: filename });
};

export default exportAmfiAsPptx;
